/**
 * ResetPasswordPage Component
 * VROONG 비밀번호 재설정 페이지
 *
 * @description
 * 이메일 링크를 통한 새 비밀번호 설정
 * URL 토큰 검증 후 새 비밀번호 입력 및 확인
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ToastPosition, ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import { extractErrorMessage } from '@/shared/utils/errorHandler';
import { LoginIcon } from '@/shared/components/icons/Icons';
import PasswordInput from '@/shared/components/forms/PasswordInput';
import PrimaryButton from '@/shared/components/buttons/PrimaryButton';
import LinkButton from '@/shared/components/buttons/LinkButton';
import { validatePassword, validatePasswordConfirmation, ValidationErrors } from '@/features/auth/lib/validation';
import { RESET_PASSWORD_FEATURES } from '@/features/auth/config/feature.config';
import { authApi } from '../../../api/authApi';
import BrandSection from '../../ui/BrandSection/BrandSection';
import VersionInfo from '../../ui/VersionInfo';
import '@/features/auth/styles/auth.css';

// ============================================================
// 상수 정의
// ============================================================

/** 유효하지 않은 토큰시 로그인 페이지 이동 지연 시간 (ms) */
const INVALID_TOKEN_REDIRECT_DELAY = 2000;

/** 토스트 기본 옵션 */
const TOAST_OPTIONS = {
    position: ToastPosition.TOP_CENTER,
} as const;

/** 성공 아이콘 컨테이너 스타일 */
const SUCCESS_ICON_STYLE: React.CSSProperties = {
    width: '80px',
    height: '80px',
    margin: '0 auto 1.5rem',
    background: 'linear-gradient(135deg, #05CD82 0%, #0091FF 100%)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

/** 성공 메시지 텍스트 스타일 */
const SUCCESS_MESSAGE_STYLE: React.CSSProperties = {
    marginTop: '1rem',
    lineHeight: '1.6',
};

/** 비밀번호 힌트 스타일 */
const PASSWORD_HINT_STYLE: React.CSSProperties = {
    marginTop: '-0.5rem',
    marginBottom: '1rem',
};

/** 로그인 버튼 스타일 */
const LOGIN_BUTTON_STYLE: React.CSSProperties = {
    marginTop: '2rem',
};

// ============================================================
// 컴포넌트 구현
// ============================================================

/**
 * 비밀번호 재설정 페이지
 */
const ResetPasswordPage: React.FC = () => {
    // ======== Hooks ========
    const { showToast } = useToast();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // URL에서 토큰 추출
    const token = searchParams.get('token');

    // ======== State ========
    const [step, setStep] = useState<'input' | 'success'>('input');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState<ValidationErrors>({});

    // ======== Memoized Values ========
    /** 브랜드 섹션 설명 텍스트 */
    const brandDescription = useMemo(() => (
        <>
            새로운 비밀번호를 설정하세요
            <br />
            안전한 비밀번호로 계정을 보호하세요
        </>
    ), []);

    // ======== Effects ========
    /** 토큰 유효성 체크 */
    useEffect(() => {
        if (!token) {
            showToast('유효하지 않은 링크입니다.', {
                type: ToastType.ERROR,
                ...TOAST_OPTIONS,
            });
            setTimeout(() => navigate('/login'), INVALID_TOKEN_REDIRECT_DELAY);
        }
    }, [token, navigate, showToast]);

    // ======== Validation ========
    /**
     * 폼 유효성 검증
     */
    const validateForm = useCallback((): boolean => {
        const errors: ValidationErrors = {};

        const passwordError = validatePassword(newPassword);
        if (passwordError) errors.newPassword = passwordError;

        const passwordConfirmError = validatePasswordConfirmation(newPassword, confirmPassword);
        if (passwordConfirmError) errors.confirmPassword = passwordConfirmError;

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    }, [newPassword, confirmPassword]);

    // ======== Event Handlers ========
    /**
     * 새 비밀번호 입력 핸들러
     */
    const handleNewPasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setNewPassword(e.target.value);
    }, []);

    /**
     * 비밀번호 확인 입력 핸들러
     */
    const handleConfirmPasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmPassword(e.target.value);
    }, []);

    /**
     * 비밀번호 재설정 폼 제출 처리
     */
    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!validateForm()) return;

        if (!token) {
            setError('유효하지 않은 토큰입니다.');
            return;
        }

        setIsLoading(true);

        try {
            const response = await authApi.resetPassword({ token, newPassword });

            showToast(response.message, {
                type: ToastType.SUCCESS,
                ...TOAST_OPTIONS,
            });

            setStep('success');
        } catch (err: unknown) {
            const errorMessage = extractErrorMessage(err, '비밀번호 재설정에 실패했습니다.');
            showToast(errorMessage, { type: ToastType.ERROR, ...TOAST_OPTIONS });
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [validateForm, token, newPassword, showToast]);

    /**
     * 로그인 페이지 이동
     */
    const handleNavigateToLogin = useCallback(() => {
        navigate('/login');
    }, [navigate]);

    // 토큰이 없으면 렌더링하지 않음
    if (!token) return null;

    // ======== Render ========
    return (
        <div className="login-page">
            <div className="login-container">
                {/* 좌측 브랜드 섹션 */}
                <BrandSection
                    title="비밀번호 재설정"
                    description={brandDescription}
                    features={RESET_PASSWORD_FEATURES}
                />

                {/* 우측 비밀번호 재설정 섹션 */}
                <div className="login-section">
                    <div className="login-form-container">
                        {step === 'input' ? (
                            <>
                                {/* 입력 단계 헤더 */}
                                <div className="login-header">
                                    <h2>비밀번호 재설정</h2>
                                    <p>새로운 비밀번호를 입력하세요</p>
                                </div>

                                {/* 비밀번호 재설정 폼 */}
                                <form className="login-form" onSubmit={handleSubmit}>
                                    {/* 새 비밀번호 */}
                                    <PasswordInput
                                        id="newPassword"
                                        name="newPassword"
                                        value={newPassword}
                                        onChange={handleNewPasswordChange}
                                        placeholder="새 비밀번호를 입력하세요"
                                        label="새 비밀번호"
                                        required
                                        disabled={isLoading}
                                        error={fieldErrors.newPassword}
                                    />
                                    <div className="password-hint" style={PASSWORD_HINT_STYLE}>
                                        8자 이상, 영문, 숫자, 특수문자 포함
                                    </div>

                                    {/* 비밀번호 확인 */}
                                    <PasswordInput
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        value={confirmPassword}
                                        onChange={handleConfirmPasswordChange}
                                        placeholder="비밀번호를 다시 입력하세요"
                                        label="비밀번호 확인"
                                        required
                                        disabled={isLoading}
                                        error={fieldErrors.confirmPassword}
                                    />

                                    {/* 에러 메시지 */}
                                    {error && <div className="error-message">{error}</div>}

                                    <PrimaryButton type="submit" loading={isLoading} disabled={isLoading}>
                                        비밀번호 재설정
                                    </PrimaryButton>
                                </form>
                            </>
                        ) : (
                            <>
                                {/* 성공 단계 헤더 */}
                                <div className="login-header">
                                    <div style={SUCCESS_ICON_STYLE}>
                                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>
                                    </div>
                                    <h2>비밀번호가 재설정되었습니다</h2>
                                    <p style={SUCCESS_MESSAGE_STYLE}>
                                        새로운 비밀번호로 로그인해주세요.
                                    </p>
                                </div>

                                <PrimaryButton
                                    onClick={handleNavigateToLogin}
                                    style={LOGIN_BUTTON_STYLE}
                                >
                                    로그인 페이지로 이동
                                </PrimaryButton>
                            </>
                        )}

                        {/* 추가 옵션 */}
                        <div className="additional-options">
                            <div className="divider">
                                <span>또는</span>
                            </div>

                            <div className="help-links">
                                <LinkButton onClick={handleNavigateToLogin}>
                                    <LoginIcon width={16} height={16} />
                                    로그인 페이지로
                                </LinkButton>
                            </div>
                        </div>
                    </div>

                    {/* 하단 버전 정보 */}
                    <VersionInfo />
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
